/**
 * Training Center — watch the narrated animation, then do it for real.
 * Three tracks: Sourcing Desk, Bid Desk, Project Management Portal.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  GraduationCap, Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Loader2,
  CheckCircle2, PlayCircle, Users, ClipboardList, Printer, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";
import AnimatedScene from "@/components/training/AnimatedScene";

const ACCENT = {
  emerald: { ring: "ring-emerald-500/40", text: "text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700", bar: "bg-emerald-400" },
  sky: { ring: "ring-sky-300", text: "text-sky-600", btn: "bg-sky-600 hover:bg-sky-700", bar: "bg-sky-400" },
  indigo: { ring: "ring-indigo-500/40", text: "text-indigo-300", btn: "bg-indigo-600 hover:bg-indigo-700", bar: "bg-indigo-400" },
};

export default function TrainingCenterPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const headers = { Authorization: `Bearer ${token}` };
  const [tracks, setTracks] = useState([]);
  const [active, setActive] = useState(null);     // track id being watched
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [records, setRecords] = useState(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/training/tracks`, { headers });
      setTracks(r.data.tracks || []);
    } catch { toast.error("Could not load the tutorials"); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { audioRef.current?.pause(); clearTimeout(timerRef.current); }, []);

  // Sidebar deep links: ?track=sourcing opens that tutorial, ?view=cards|records
  const wanted = params.get("track");
  const view = params.get("view");
  useEffect(() => {
    if (!tracks.length) return;
    if (wanted && tracks.some((t) => t.id === wanted)) {
      setActive(wanted); setIdx(0); setPlaying(true);
    } else {
      setActive(null); setPlaying(false);
      if (view === "records") openRecords();
    }
  }, [wanted, view, tracks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCard = (trackId) =>
    openAuthedDoc(trackId ? `/training/quick-card/${trackId}` : "/training/quick-cards", { token });

  const track = tracks.find((t) => t.id === active);
  const step = track?.steps?.[idx];
  const accent = ACCENT[track?.accent] || ACCENT.sky;

  const record = useCallback(async (trackId, stepId, done = false) => {
    try {
      await axios.post(`${API}/training/progress`,
        { track_id: trackId, step_id: stepId, kind: "watched", completed_track: done },
        { headers });
    } catch { /* never blocks training */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Play one step: fetch its narration, speak it, then advance.
  const playStep = useCallback(async (trackId, stepIndex) => {
    const t = tracks.find((x) => x.id === trackId);
    const s = t?.steps?.[stepIndex];
    if (!s) return;
    clearTimeout(timerRef.current);
    audioRef.current?.pause();
    record(trackId, s.id, stepIndex === t.steps.length - 1);

    const advance = () => {
      if (stepIndex + 1 < t.steps.length) {
        setIdx(stepIndex + 1);
      } else {
        setPlaying(false);
        toast.success("Tutorial complete — it is on your training record");
        load();
      }
    };

    if (muted) {
      timerRef.current = setTimeout(advance, 6500);
      return;
    }
    setLoadingAudio(true);
    try {
      const r = await axios.get(`${API}/training/narration/${trackId}/${s.id}`, { headers });
      const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
      audioRef.current = a;
      a.onended = advance;
      try {
        await a.play();
      } catch {
        // Browsers block autoplay until the page has been interacted with —
        // fall back to captions on a timer, no error worth shouting about.
        timerRef.current = setTimeout(advance, 6500);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Narration unavailable — showing captions only");
      timerRef.current = setTimeout(advance, 6500);
    } finally { setLoadingAudio(false); }
  }, [tracks, muted, record, load]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (playing && active) playStep(active, idx);
    if (!playing) { audioRef.current?.pause(); clearTimeout(timerRef.current); }
  }, [playing, idx, active]); // eslint-disable-line react-hooks/exhaustive-deps

  const openRecords = async () => {
    try {
      const r = await axios.get(`${API}/training/records`, { headers });
      setRecords(r.data);
    } catch { toast.error("Training records are admin-only"); }
  };

  const isAdmin = ["admin", "super_admin", "hub_admin"].includes(user?.role)
    && !user?.demo_readonly
    && !(user?.permissions || []).includes("read_only_demo");

  return (
    <div className="min-h-screen bg-[#f6f9fc] text-slate-900" data-testid="training-center">
      <div className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-sky-700">
              Sterile Mate · operator training
            </p>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight mt-2">
              Training Center
            </h1>
            <p className="text-base text-slate-700 mt-3 max-w-2xl">
              Watch the narrated walkthrough of a desk end to end, then run the same steps on
              the live screens with the guide looking over your shoulder.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-slate-300 text-slate-800 hover:bg-slate-100"
              onClick={() => openCard(null)} data-testid="training-print-all-cards">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print all 3 cards
            </Button>
            {isAdmin && (
              <Button size="sm" variant="outline" className="border-slate-300 text-slate-800 hover:bg-slate-100"
                onClick={openRecords} data-testid="training-records-btn">
                <Users className="w-3.5 h-3.5 mr-1" /> Training records
              </Button>
            )}
          </div>
        </div>

        {/* track cards */}
        {!active && (
          <div className="grid md:grid-cols-3 gap-4 mt-10" data-testid="training-track-list">
            {tracks.map((t, i) => {
              const a = ACCENT[t.accent] || ACCENT.sky;
              const watched = (t.progress?.watched_steps || []).length;
              const pct = Math.round((watched / Math.max(t.step_count, 1)) * 100);
              return (
                <motion.div key={t.id} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.45 }}
                  className={`rounded-2xl bg-white ring-1 ${a.ring} p-5 flex flex-col`}
                  data-testid={`training-card-${t.id}`}>
                  <p className={`text-[10px] font-black uppercase tracking-[0.16em] ${a.text}`}>
                    {t.step_count} steps
                  </p>
                  <h2 className="text-lg font-black mt-1.5 leading-snug">{t.name}</h2>
                  <p className="text-[13px] text-slate-700 mt-2 flex-1">{t.blurb}</p>
                  <div className="h-1.5 rounded-full bg-slate-100 mt-4 overflow-hidden">
                    <div className={`h-1.5 ${a.bar}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[11px] text-slate-600 mt-1.5">
                    {t.progress?.completed_at
                      ? `Completed ${new Date(t.progress.completed_at).toLocaleDateString()}`
                      : `${watched} of ${t.step_count} watched`}
                  </p>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <Button size="sm" className={`h-8 text-xs ${a.btn}`}
                      onClick={() => { setActive(t.id); setIdx(0); setPlaying(true); }}
                      data-testid={`training-watch-${t.id}`}>
                      <PlayCircle className="w-3.5 h-3.5 mr-1" /> Watch
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 text-slate-800 hover:bg-slate-100"
                      onClick={() => navigate(`${t.route}?walkthrough=${t.id}`)}
                      data-testid={`training-walk-${t.id}`}>
                      Do it live
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-700 hover:bg-slate-100"
                      onClick={() => openCard(t.id)}
                      data-testid={`training-card-pdf-${t.id}`}>
                      <Printer className="w-3.5 h-3.5 mr-1" /> Card
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* quick reference cards */}
        {!active && (
          <div id="quick-cards" className="mt-8 rounded-2xl bg-white ring-1 ring-slate-200 p-5"
            data-testid="training-quick-cards">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.16em] text-amber-700">
                  Printable
                </p>
                <h2 className="text-lg font-black mt-1">Quick reference cards</h2>
                <p className="text-[13px] text-slate-700 mt-1.5 max-w-2xl">
                  One page per track — every step in order with where it lives on the screen, the
                  rules you must not skip, and what to do when something will not go through. Print
                  it and keep it at the desk.
                </p>
              </div>
              <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700"
                onClick={() => openCard(null)} data-testid="training-cards-print-all">
                <Printer className="w-3.5 h-3.5 mr-1" /> Print all 3
              </Button>
            </div>
            <div className="grid sm:grid-cols-3 gap-2 mt-4">
              {tracks.map((t) => (
                <button key={t.id} onClick={() => openCard(t.id)}
                  className="text-left rounded-xl bg-[#f6f9fc] ring-1 ring-slate-200 hover:ring-amber-500/50 p-3 transition-colors"
                  data-testid={`training-card-link-${t.id}`}>
                  <FileText className="w-4 h-4 text-amber-700" />
                  <p className="text-[13px] font-bold text-slate-900 mt-2 leading-snug">
                    {t.name.split(" — ")[0]}
                  </p>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    {t.step_count} steps · 1 page PDF
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* player */}
        {track && step && (
          <div className="mt-8" data-testid="training-player">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className={`text-[10px] font-black uppercase tracking-[0.16em] ${accent.text}`}>
                  {track.name} · step {idx + 1} of {track.steps.length}
                </p>
                <h2 className="text-2xl font-black mt-1">{step.title}</h2>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button size="sm" variant="ghost" className="text-slate-700 hover:bg-slate-100"
                  onClick={() => { setPlaying(false); setIdx(Math.max(0, idx - 1)); }}
                  data-testid="training-prev">
                  <SkipBack className="w-4 h-4" />
                </Button>
                <Button size="sm" className={`${accent.btn}`} onClick={() => setPlaying(!playing)}
                  data-testid="training-playpause">
                  {loadingAudio ? <Loader2 className="w-4 h-4 animate-spin" />
                    : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" className="text-slate-700 hover:bg-slate-100"
                  onClick={() => { setPlaying(false); setIdx(Math.min(track.steps.length - 1, idx + 1)); }}
                  data-testid="training-next">
                  <SkipForward className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-slate-700 hover:bg-slate-100"
                  onClick={() => { setMuted(!muted); audioRef.current?.pause(); }}
                  data-testid="training-mute">
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 text-slate-800 hover:bg-slate-100"
                  onClick={() => openCard(track.id)} data-testid="training-player-card">
                  <Printer className="w-3.5 h-3.5 mr-1" /> Card
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs border-slate-300 text-slate-800 hover:bg-slate-100"
                  onClick={() => { setPlaying(false); audioRef.current?.pause(); setActive(null); setParams({}); load(); }}
                  data-testid="training-exit">
                  All tracks
                </Button>
              </div>
            </div>

            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 mt-5">
              <motion.div key={`${track.id}-${step.id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}>
                <AnimatedScene scene={step.scene} sceneKey={`${track.id}-${step.id}`} />
                <div className="mt-4 rounded-xl bg-white ring-1 ring-slate-200 p-4">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-600">Caption</p>
                  <p className="text-[14px] text-slate-800 mt-1">{step.caption}</p>
                  <p className="text-[13px] text-slate-700 mt-2 leading-relaxed" data-testid="training-narration-text">
                    {step.narration}
                  </p>
                  {step.route && step.testid && (
                    <Button size="sm" variant="outline" className="h-7 text-xs mt-3 border-slate-300 text-slate-800 hover:bg-slate-100"
                      onClick={() => navigate(`${track.route}?walkthrough=${track.id}`)}
                      data-testid="training-try-live">
                      Try this step live
                    </Button>
                  )}
                </div>
              </motion.div>

              <div className="rounded-xl bg-white ring-1 ring-slate-200 p-3 h-fit">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-600 px-1 mb-1.5">
                  Steps
                </p>
                <div className="space-y-0.5 max-h-[520px] overflow-y-auto">
                  {track.steps.map((s, i) => (
                    <button key={s.id} onClick={() => { setPlaying(false); setIdx(i); }}
                      className={`w-full text-left px-2.5 py-2 rounded-lg text-[12.5px] transition-colors ${i === idx
                        ? "bg-sky-700 text-white font-bold" : "text-slate-700 hover:bg-slate-100"}`}
                      data-testid={`training-step-${s.id}`}>
                      <span className="inline-flex items-center gap-1.5">
                        {(track.progress?.watched_steps || []).includes(s.id)
                          ? <CheckCircle2 className={`w-3.5 h-3.5 shrink-0 ${i === idx ? "text-emerald-200" : "text-emerald-600"}`} />
                          : <span className={`w-3.5 text-[10px] font-bold ${i === idx ? "text-sky-100" : "text-slate-500"}`}>{i + 1}</span>}
                        {s.title}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* admin records */}
        {records && (
          <div className="mt-10 rounded-xl bg-white ring-1 ring-slate-200 p-4" data-testid="training-records">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black inline-flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-sky-700" /> Training records
              </h3>
              <button className="text-[11px] text-slate-600 hover:underline" onClick={() => setRecords(null)}>
                Hide
              </button>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr>
                    {["User", "Role", "Track", "Watched", "Walked", "Completed"].map((h) => (
                      <th key={h} className="text-left px-2 py-1.5 text-[9.5px] font-black uppercase tracking-wider text-slate-600">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.records.flatMap((r) => r.tracks.map((t, j) => (
                    <tr key={`${r.user_email}-${t.track_id}`} className="border-t border-slate-200">
                      <td className="px-2 py-1.5 text-[12px]">{j === 0 ? (r.user_name || r.user_email) : ""}</td>
                      <td className="px-2 py-1.5 text-[11px] text-slate-600">{j === 0 ? r.user_role : ""}</td>
                      <td className="px-2 py-1.5 text-[11.5px]">{t.track_name}</td>
                      <td className="px-2 py-1.5 text-[11.5px]">{t.watched}/{t.step_count}</td>
                      <td className="px-2 py-1.5 text-[11.5px]">{t.walked}</td>
                      <td className="px-2 py-1.5 text-[11.5px]">
                        {t.completed_at
                          ? <span className="text-emerald-600 font-bold">{new Date(t.completed_at).toLocaleDateString()}</span>
                          : <span className="text-slate-600">in progress</span>}
                      </td>
                    </tr>
                  )))}
                  {!records.records.length && (
                    <tr><td colSpan={6} className="px-2 py-5 text-center text-sm text-slate-600">
                      Nobody has started a tutorial yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-700 mt-10 inline-flex items-center gap-1.5">
          <GraduationCap className="w-3.5 h-3.5" />
          Narration is generated once per step and cached, so replays cost nothing.
        </p>
      </div>
    </div>
  );
}
